import React from 'react'

import { useLoaderData, useNavigate } from 'react-router-dom'

import WebApp from '@twa-dev/sdk'

import { QueryView } from '@/components/base/queryView'
import {
    cachedQuery,
    fixedQuery,
    MINUTE,
    revalidatedQuery,
    type Query
} from '@/lib/queryCache'
import { toCoachPreviews } from '@/lib/community/coachPreview'
import {
    coachRequestsQuery,
    sentRequestsQuery,
    workoutHistoryQuery
} from '@/lib/ownQueries'
import { ownKey } from '@/lib/ownKey'
import { useIsCoach } from '@/hooks/useAuthSession'
import { useUserContext } from '@/hooks/user'

import addDays from '@/lib/date'
import { cn } from '@/lib/utils'
import { fill } from '@/lib/i18n/fill'
import {
    HARD_LEVEL,
    complexProgrammeIds,
    inRankOrder,
    rankedFirst,
    rankByTotal,
    requestsReceived,
    workoutsDone
} from '@/lib/analytics/queryItems'
import { PERIODS, STATS_WINDOW } from '@/lib/analytics/periods'
import { getPremiumAccessTimelines, getUserId, isCoach } from '@/lib/cookies'
import type { V1SMQueryItem } from '@/lib/api/lib'
import { authTokenLocalStorageKey } from '@/lib/api/lib/client'
import type { V1ProgrammeRequestList } from '@/lib/api/lib/models'
import { ProgramPreview, lessonPreviewClient } from '@/lib/api/community'
import { resolveProgrammeSource } from '@/lib/api/sharedProgramme'
import { profileClient } from '@/lib/api/profileClient'
import type { V1ProCoaches } from '@/domain/ProCoaches'
import { localStorageClient } from '@/lib/db/local/client'
import { referralsApiClient } from '@/lib/api/lib/client'
import { resolveStartTarget } from '@/lib/share/startTarget'

import { PersonalScheduleProgram, Program } from '@/domain'
import type { ProgrammeRefData } from '@/lib/api/sharedProgramme'
import { mergePersonalScheduleItemGroups } from '@/lib/programmeSnapshots'
import { isActivePersonalSchedule } from '@/lib/activeSchedule'
import {
    loadAllDatalakeItems,
    loadAllProgrammeProgressItems
} from '@/lib/programmeSnapshotLoader'

import { AppContext } from '@/components/base/appcontext'
import LinkWithState from '@/components/base/link'
import TrainingHistory from '@/components/profile/history'
import TrandsPanel from '@/components/community/trandsPanel'
import CoachesCarousel, {
    CoachPreview
} from '@/components/community/coachesCarousel'
import CoachFlyer from '@/components/community/coachFlyer'
import { LeaderboardTeaser } from '@/components/leaderboard/teaser'
import { loadTeaser, type TeaserData } from '@/lib/leaderboard'
import { LEADERBOARD_ENABLED } from '@/lib/flags'
import VacantSeatCard from '@/components/community/vacantSeatCard'
import ListItem from '@/components/schedule/listItem'
import ActiveTraining from '@/components/profile/activetraining'
import OngoingWorkoutAlert from '@/components/training/ongoingWorkoutAlert'
import CoachDashboard from '@/components/requests/coachDashboard'
import UserRequests from '@/components/requests/userRequests'
import UndefinedUser from '@/components/base/undefinedUser'

import UserAvatar from '@/components/base/userAvatar'
import { CircularProgress } from '@/components/ui/circular-progress'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'

import {
    Target,
    CalendarDays,
    Radio,
    Flame,
    Zap,
    Users,
    Trophy,
    type LucideIcon
} from 'lucide-react'

type Trendings = {
    popular: ProgramPreview[]
    complex: ProgramPreview[]
}

type HomeData = {
    // The user's own data: last copy at once, always refetched.
    dl: Query<{ ps: Program[]; pps: PersonalScheduleProgram[] }>
    workoutHistory: Query<V1SMQueryItem[]>
    sentRequests: Query<V1ProgrammeRequestList>
    coachRequests: Query<V1ProgrammeRequestList | undefined>
    leaderboard: Query<TeaserData | undefined>
    // Public data: served from cache within its ttl.
    trending: Query<Trendings>
    topCoaches: Query<CoachPreview[]>
}

// Coaches the home "Top Coaches" carousel shows.
const TOP_COACHES = 6

export async function homeLoader(): Promise<HomeData> {
    const token = localStorage.getItem(authTokenLocalStorageKey)
    if (!token) {
        return {
            dl: fixedQuery({ ps: [], pps: [] }),
            workoutHistory: fixedQuery([]),
            trending: fixedQuery({ popular: [], complex: [] }),
            topCoaches: fixedQuery([]),
            sentRequests: fixedQuery({ items: [] }),
            coachRequests: fixedQuery(undefined),
            leaderboard: fixedQuery(undefined)
        }
    }

    const dlLoader = async () => {
        const lastUpdatedAfter = Math.round(
            addDays(new Date(), -90).getTime() / 1000
        )
        const [items, progress] = await Promise.all([
            loadAllDatalakeItems({
                kind: ['p.s', 'p.ps', 'p.ref'],
                lastUpdatedAfter
            }),
            loadAllProgrammeProgressItems({ lastUpdatedAfter })
        ])

        // Build Programme stubs from p.ref items so active workouts can match
        const sharedPs: Program[] = items
            .filter((e) => e.kind === 'p.ref')
            .map((e) => {
                const ref = JSON.parse(e.data) as ProgrammeRefData
                return {
                    id: e.corellationId || e.id,
                    originalProgramId: e.corellationId || e.id,
                    name: ref.programmeName || 'Shared Programme',
                    lessons: [],
                    schedule: [],
                    published: false,
                    status: 'draft',
                    cost: 0
                } as unknown as Program
            })

        const ownedPs = items
            .filter((e) => e.kind === 'p.s')
            .map((e) => JSON.parse(e.data) as Program)
        const pps = mergePersonalScheduleItemGroups([...items, ...progress])

        // The p.ref stubs above carry no lessons/schedule, so ListItem
        // cannot render an active shared programme from them (it treats
        // an empty schedule as "finished"). Owned p.s items can also fall
        // outside the 90-day window while their p.ps is still active.
        // Resolve the full source for every active schedule that has no
        // usable programme yet — same path the agenda route uses.
        const knownIds = new Set(ownedPs.map((e) => e.id))
        const missing = pps.filter(
            (e) =>
                isActivePersonalSchedule(e) &&
                e.programId &&
                !knownIds.has(e.programId)
        )
        const resolved = await Promise.all(
            missing.map((e) =>
                resolveProgrammeSource(e.programId).catch((error) => {
                    console.error(
                        'Failed to resolve active programme source:',
                        error
                    )
                    return undefined
                })
            )
        )
        const resolvedPs = resolved.filter((e): e is Program => e !== undefined)
        const resolvedIds = new Set(resolvedPs.map((e) => e.id))

        return {
            ps: [
                ...ownedPs,
                ...resolvedPs,
                ...sharedPs.filter((e) => !resolvedIds.has(e.id))
            ],
            pps
        }
    }

    const topCoachesLoader = async (): Promise<CoachPreview[]> => {
        // The newest coaches pad a short ranking (a quiet quarter still
        // shows who coaches here); read alongside it.
        const newest = profileClient.listProCoaches(1, TOP_COACHES)
        newest.catch(() => {})
        // Most plan requests over the stats window.
        const requests = await requestsReceived(STATS_WINDOW)
        const topCoachIds = rankByTotal(requests, 'userId').slice(
            0,
            TOP_COACHES
        )
        const [ranked, padding] = await Promise.all([
            topCoachIds.length
                ? profileClient.listProCoaches(
                      1,
                      topCoachIds.length,
                      `(${topCoachIds.map((id) => `id='${id}'`).join('||')})`
                  )
                : { items: [] },
            newest
        ])
        const key = (c: V1ProCoaches) => c.expand?.coach?.id
        const coaches = new Map(
            [...ranked.items, ...padding.items].map((c) => [key(c), c])
        )
        return toCoachPreviews(
            rankedFirst([...coaches.values()], topCoachIds, key).slice(
                0,
                TOP_COACHES
            )
        )
    }

    const trendingLoader = async (): Promise<Trendings> => {
        // Today's top three by workouts done and by hard ratings: the same
        // lookups as the programme list's trends and complexity filters.
        const [popularData, complexIds] = await Promise.all([
            workoutsDone(PERIODS.today, 'pId'),
            complexProgrammeIds(PERIODS.today, HARD_LEVEL)
        ])
        const popular = rankByTotal(popularData, 'pId').slice(0, 3)
        const complex = complexIds.slice(0, 3)
        const allprogrammes = [...popular, ...complex].map(
            (e) => `origId='${e}'`
        )

        if (allprogrammes.length === 0) {
            return { popular: [], complex: [] }
        }

        const previews = await lessonPreviewClient.fetchProgramPreviews(
            1,
            20,
            undefined,
            `(${allprogrammes.join('||')})`
        )

        return {
            popular: inRankOrder(previews.items, popular, (e) => e.origId),
            complex: inRankOrder(previews.items, complex, (e) => e.origId)
        }
    }

    const userId = getUserId()
    return {
        dl: revalidatedQuery(ownKey('home:programmes'), dlLoader),
        workoutHistory: workoutHistoryQuery(),
        sentRequests: sentRequestsQuery(),
        coachRequests: isCoach() ? coachRequestsQuery() : fixedQuery(undefined),
        leaderboard: LEADERBOARD_ENABLED
            ? revalidatedQuery(
                  ownKey('leaderboard:teaser'),
                  () => loadTeaser(userId),
                  ['own', 'stats']
              )
            : fixedQuery(undefined),
        trending: cachedQuery('community:home:trending', trendingLoader, {
            ttl: 5 * MINUTE,
            tags: ['stats', 'previews', 'authors'],
            keep: (data) => data.popular.length + data.complex.length > 0
        }),
        topCoaches: cachedQuery('community:home:topCoaches', topCoachesLoader, {
            ttl: 5 * MINUTE,
            tags: ['stats', 'coaches', 'authors'],
            fallback: () => []
        })
    }
}

function calculateProgress(issueTime: number, deadlineTime: number): number {
    const todayTime = new Date().getTime() / 1000

    const totalDuration = deadlineTime - issueTime
    if (totalDuration === 0) {
        return todayTime >= deadlineTime ? 100 : 0
    }

    const elapsed = todayTime - issueTime
    const progress = (elapsed / totalDuration) * 100

    return Math.min(100, Math.max(0, progress))
}

// Literal class names so Tailwind picks them up.
const SECTION_COLORS = {
    amber: ['text-amber-600 dark:text-amber-400', 'bg-amber-500/10'],
    sky: ['text-sky-600 dark:text-sky-400', 'bg-sky-500/10'],
    rose: ['text-rose-600 dark:text-rose-400', 'bg-rose-500/10'],
    indigo: ['text-indigo-600 dark:text-indigo-400', 'bg-indigo-500/10'],
    orange: ['text-orange-600 dark:text-orange-400', 'bg-orange-500/10'],
    violet: ['text-violet-600 dark:text-violet-400', 'bg-violet-500/10'],
    yellow: ['text-yellow-600 dark:text-yellow-400', 'bg-yellow-500/10']
} as const

// Home section: tinted icon, title, optional subtitle, then the content.
function Section({
    icon: Icon,
    color,
    titleSlug,
    subtitleSlug,
    children
}: {
    icon: LucideIcon
    color: keyof typeof SECTION_COLORS
    titleSlug: string
    subtitleSlug?: string
    children: React.ReactNode
}) {
    const appContext = React.useContext(AppContext)
    const [iconColor, tint] = SECTION_COLORS[color]
    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
                <div
                    className={cn(
                        'h-7 w-7 rounded-lg flex items-center justify-center shrink-0',
                        tint
                    )}
                >
                    <Icon className={cn('h-3.5 w-3.5', iconColor)} />
                </div>
                <div className="flex-1 min-w-0">
                    <Typography variant="title-sm" className="leading-tight">
                        {appContext.geti18n(titleSlug)}
                    </Typography>
                    {subtitleSlug && (
                        <Typography variant="body-xs" className="leading-tight">
                            {appContext.geti18n(subtitleSlug)}
                        </Typography>
                    )}
                </div>
            </div>
            {children}
        </div>
    )
}

const ONE_SKELETON = <Skeleton className="rounded-xl h-28" />
const TWO_SKELETONS = (
    <>
        <Skeleton className="rounded-xl h-28" />
        <Skeleton className="rounded-xl h-14" />
    </>
)

function Home() {
    const appContext = React.useContext(AppContext)
    const navigate = useNavigate()
    const userContext = useUserContext()
    const {
        dl,
        workoutHistory,
        trending,
        topCoaches,
        sentRequests,
        coachRequests,
        leaderboard
    } = useLoaderData<HomeData>()

    // StrictMode runs effects twice in dev; one resolution per mount.
    const resolvingStart = React.useRef(false)
    React.useEffect(() => {
        const checkInitValues = async () => {
            if (resolvingStart.current) return
            resolvingStart.current = true
            const initState = await localStorageClient.retrieveInitState()
            if (!initState) return
            const target = await resolveStartTarget(
                initState,
                referralsApiClient
            )
            // A transient lookup failure keeps the start state, so coming
            // back home later in the launch tries the link again.
            if (!target.retry) await localStorageClient.flushInitState()
            if (target.invitedBy) {
                WebApp.showAlert?.(
                    fill(appContext.geti18n('referral.invite.invitedBy'), {
                        login: target.invitedBy
                    })
                )
            }
            if (target.path) navigate(target.path)
        }
        checkInitValues()
    }, [])

    const isCoachUser = useIsCoach()

    if (!userContext.user) return <UndefinedUser />

    const {
        isPremium,
        issued: premiumIssued,
        deadline: premiumDeadline
    } = getPremiumAccessTimelines()

    const avatar = (
        <LinkWithState to={`userProfile/`}>
            <UserAvatar
                name={userContext.user.username}
                photo={userContext.user.photo_url}
                className="h-10 w-10 shrink-0"
                fallbackClassName="text-sm font-semibold"
            />
        </LinkWithState>
    )

    // Non-coaches still get the section, for the vacant seat card.
    const renderTopCoaches = (coaches: CoachPreview[]) =>
        (coaches.length > 0 || !isCoachUser) && (
            <Section
                icon={Users}
                color="indigo"
                titleSlug="homepage.topCoaches.title"
            >
                <CoachesCarousel
                    items={coaches}
                    seeMoreLink="/coaches/list"
                    trailing={<VacantSeatCard />}
                />
            </Section>
        )

    const renderTrending = (data: Trendings) => (
        <>
            <Section
                icon={Flame}
                color="orange"
                titleSlug="homepage.trendingToday.title"
            >
                <TrandsPanel
                    items={data.popular}
                    seeMoreLink="/communityProgrammes/list?trends=today"
                />
            </Section>
            <Section
                icon={Zap}
                color="violet"
                titleSlug="homepage.mostComplexToday.title"
            >
                <TrandsPanel
                    items={data.complex}
                    seeMoreLink={`/communityProgrammes/list?complexity=${HARD_LEVEL}&period=today`}
                />
            </Section>
        </>
    )

    return (
        <div className="flex flex-col items-stretch gap-4">
            <div className="flex flex-row justify-between items-center">
                <Typography variant="title-lg">
                    {`${appContext.geti18n('home.title.welcome.text')} ${userContext.user.username || ''}`}
                </Typography>
                {isPremium ? (
                    <CircularProgress
                        value={
                            100 -
                            calculateProgress(premiumIssued, premiumDeadline)
                        }
                    >
                        {avatar}
                    </CircularProgress>
                ) : (
                    avatar
                )}
            </div>

            <OngoingWorkoutAlert />

            <QueryView
                query={coachRequests}
                fallback={<Skeleton className="rounded-xl h-32" />}
                errorElement={<></>}
            >
                {(data) =>
                    data && <CoachDashboard requests={data.items || []} />
                }
            </QueryView>

            <QueryView
                query={sentRequests}
                fallback={<Skeleton className="rounded-xl h-14" />}
                errorElement={<></>}
            >
                {(data) => <UserRequests requests={data.items || []} />}
            </QueryView>

            <Section
                icon={Target}
                color="amber"
                titleSlug="home.title.activeProgrammes.text"
            >
                <QueryView query={dl} fallback={TWO_SKELETONS}>
                    {(data) => (
                        <ListItem
                            workoutList={data.ps}
                            personalSchedule={data.pps?.filter(
                                isActivePersonalSchedule
                            )}
                            isActive
                        />
                    )}
                </QueryView>
            </Section>

            <Section
                icon={CalendarDays}
                color="sky"
                titleSlug="home.title.history.text"
            >
                <QueryView query={workoutHistory} fallback={ONE_SKELETON}>
                    {(data) => <TrainingHistory history={data} />}
                </QueryView>
            </Section>

            {LEADERBOARD_ENABLED && (
                <QueryView
                    query={leaderboard}
                    fallback={ONE_SKELETON}
                    errorElement={<></>}
                >
                    {(data) =>
                        data && (
                            <Section
                                icon={Trophy}
                                color="yellow"
                                titleSlug="leaderboard.title"
                            >
                                <LeaderboardTeaser data={data} />
                            </Section>
                        )
                    }
                </QueryView>
            )}

            <Section
                icon={Radio}
                color="rose"
                titleSlug="home.title.livestream.text"
                subtitleSlug="home.title.livestream.subText"
            >
                <ActiveTraining userId={getUserId()} />
            </Section>

            <QueryView query={topCoaches} fallback={ONE_SKELETON}>
                {renderTopCoaches}
            </QueryView>

            <QueryView query={trending} fallback={TWO_SKELETONS}>
                {renderTrending}
            </QueryView>

            <CoachFlyer />
        </div>
    )
}

export default Home
